import type { CoachingSession } from '@efdigital/ll-addon-data';
import type AdminSessionsService from '@efdigital/ll-admin-v2/services/admin-sessions';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { AdminActionModal } from './admin-action-modal.gts';

interface SessionAbandonModalSignature {
  Args: {
    coachingSession: CoachingSession;
    /** Called after abandoning when the audit row was retained — refresh in place. */
    onSuccess?: () => Promise<void>;
    /** Called after abandoning when the row was deleted — the session no longer exists. */
    onAbandoned?: () => Promise<void>;
  };
  Blocks: {
    abandon: [showModal: () => void];
  };
}

export class SessionAbandonModal extends Component<SessionAbandonModalSignature> {
  @service declare adminSessions: AdminSessionsService;

  abandon = async (deleteRow: boolean) => {
    await this.adminSessions.abandonSession(this.args.coachingSession.id!, {
      deleteRow,
    });
    if (deleteRow) {
      await this.args.onAbandoned?.();
    } else {
      await this.args.onSuccess?.();
    }
  };

  setDeleteRowFromInput = (
    setValue: (value: boolean) => void,
    event: Event
  ) => {
    setValue((event.target as HTMLInputElement).checked);
  };

  <template>
    <AdminActionModal
      @title="Abandon booking"
      @submitLabel="Abandon booking"
      @submittingLabel="Abandoning…"
      @initialValue={{false}}
      @onSubmit={{this.abandon}}
    >
      <:trigger as |showModal|>
        {{yield showModal to="abandon"}}
      </:trigger>
      <:description>
        Force-abandons a booking stuck in
        <code>booking_pending</code>
        — e.g. after a Lambda crash mid-booking. No quota is debited or
        credited, and any existing Cronofy event is left untouched.
      </:description>
      <:fields as |deleteRow setValue isSubmitting|>
        <div class="form-check mb-3">
          <input
            id="abandon-delete-row"
            class="form-check-input"
            type="checkbox"
            checked={{deleteRow}}
            disabled={{isSubmitting}}
            data-test-abandon-delete-row
            {{on "change" (fn this.setDeleteRowFromInput setValue)}}
          />
          <label class="form-check-label" for="abandon-delete-row">
            Also delete this session record entirely
          </label>
          <div class="form-text">Leave unchecked to keep an audit row marked
            <code>booking_abandoned</code>.</div>
        </div>
      </:fields>
    </AdminActionModal>
  </template>
}
